import get from 'lodash/get';
import {
  all,
  call,
  cancelled,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import { clearLocalStorage } from 'legacy-packages/ft-cb';
import { me as meQuery } from 'legacy-src/queries';
import {
  isOnLogin,
  isOnRoot,
  isOnJoin,
  isOnLogout,
} from 'legacy-src/utility/UrlPaths';
import { pageLoading } from 'legacy-src/actions/commonActions';
import renderValidation from 'legacy-src/services/error';
import log from 'chameleon/ui-stack/utilities/log';
import fetchFlags from 'legacy-src/utility/featureFlag/fetchFlags';
import { toast } from 'chameleon/ui-stack/ui/toast';
import LOGOUT_WHITELIST from 'chameleon/ui-stack/constants/lc-logout-whitelist';
import {
  levityLoginAction,
  authenticateStrategyAction,
  navigateToError,
  redirectToHomeAction,
  authorize,
  login,
  redirectToROSLoginAction,
  redirectToLoginAction,
  storeCbSessionAction,
} from './actions';
import {
  getCbSession,
  loginWithLevity,
  setGlobalCurrentUser,
  isLockdownBrowser,
  LOCKDOWN_PAGES,
  sendForAuthentication,
  initHeap,
} from './utils';

const selectCbSession = (state) => state.auth.cbSession;
const getApolloClient = (state) => state.auth.apolloClient;
const getUserData = (state) => state.userData;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isLocalDevelopment = process.env.ENV === 'dev';

export function* redirectToROS() {
  try {
    yield delay(3000);
  } finally {
    if (!(yield cancelled())) {
      yield put(redirectToROSLoginAction());
    }
  }
}

export function* redirectToROSLoginSaga() {
  if (!isLocalDevelopment) {
    log(`redirecting to ${ROS_FRONTEND_URL}`);
    yield call(sendForAuthentication, window.location.href);
  } else {
    const directLogin = `${FYM_FRONTEND_URL}/login?directlogin=true`;
    log(`redirecting to ${directLogin}}`);
    yield call(() => (window.location.href = directLogin));
  }
}

export function* authenticateStrategySaga({ payload }) {
  try {
    const userData = yield select(getUserData);
    // we grab cbSession from redux here. this might not be populated by levity yet.
    const cbSession = yield select(selectCbSession);
    const token = window.localStorage.getItem('account_access_token');
    const {
      location: {
        pathname,
        query: { account_updated: accountUpdated, directlogin },
      },
      params,
      router,
    } = payload;
    const error_id = renderValidation(CURRENT_PILOT_NAME, userData, params);
    const splitPathname = pathname.split('/');
    const isLockdownPage = LOCKDOWN_PAGES.includes(splitPathname[1]);
    /**
     * set currentPath to go back to it if later if you are currently not logged in
     * and prevent setting circular redirects like / or /login or /logout.
     */
    const cbSessionIsLoggedIn = get(cbSession, 'isLoggedIn');
    if (!isOnLogout(pathname) && error_id > -1) {
      yield put(navigateToError());
      router.replace(`/error/${error_id}`);
    } else if (accountUpdated) {
      /**
       * Executed when user updates their info in My Account in amerit-nuevo
       * Needs to logout user and send them back to login
       */
      clearLocalStorage(LOGOUT_WHITELIST);
      router.replace('/login?account_updated_notify=true');
      // this path is the typical cb sso path (no token, levity populated levity object)
    } else if (!token && cbSessionIsLoggedIn) {
      yield put(levityLoginAction(payload));
    } else if (
      !token &&
      !isLockdownPage &&
      !cbSessionIsLoggedIn &&
      (isOnRoot(pathname) || (isOnLogin(pathname) && !directlogin))
    ) {
      yield race([
        // adding artificial timeout to give levity a chance to login (according to Lev, this is necessary)
        call(redirectToROS),
        take(levityLoginAction.TRIGGER),
      ]);
    } else if (
      !token &&
      !isLockdownPage &&
      !cbSessionIsLoggedIn &&
      !(isOnJoin(pathname) || isOnLogin(pathname))
    ) {
      // we are going to redirect to login UNLESS we receive a levity login event within 3 seconds.
      // this means cb session object in redux might be outdated at this point if levity login event occurs
      yield race([
        // adding artificial timeout to give levity a chance to login (according to Lev, this is necessary)
        call(redirectToROS),
        take(levityLoginAction.TRIGGER),
      ]);
      // for sso login
    } else if (token && cbSessionIsLoggedIn) {
      yield put(levityLoginAction(payload));
      // for direct login, we skip levity
    } else if (token && !cbSessionIsLoggedIn) {
      yield put(authorize(payload));
    } else {
      yield put(pageLoading.fulfill());
    }
  } catch (error) {
    log('authenticateStrategySaga error', error);
    yield put(authenticateStrategyAction.failure(error));
    yield put(pageLoading.fulfill());
  }
}

export function* serviceAccountsSaga({ payload }) {
  try {
    // We need a refreshed CB object in the case where levity login event (from levity)
    // is triggereed after we store it into redux. (see race side-effect handler)
    const cbSession = getCbSession();

    // store new cb session object
    yield put(storeCbSessionAction(cbSession));

    const token = window.localStorage.getItem('account_access_token');
    let am_user_id = 1;
    if (!token) {
      toast('Please wait while you are being logged in...', { timeout: 7 });
    } else {
      am_user_id = JSON.parse(atob(token.split('.')[1])).identity.am_user_id;
    }

    const { use_cb_redirect_url } = yield fetchFlags(
      ['use_cb_redirect_url'],
      am_user_id,
    );

    const serviceAccountsResponse = yield loginWithLevity(
      cbSession,
      use_cb_redirect_url === 'on',
    );
    yield put(levityLoginAction.success(serviceAccountsResponse));
    yield put(authorize(payload));
  } catch (error) {
    log('serviceAccountsSaga error', error);
    yield put(levityLoginAction.failure(error));
  }
}

export function* authorizeServiceSaga({ type, payload }) {
  const isLogin = type === login.TRIGGER;
  const cbSessionIsLoggedIn = !isLocalDevelopment
    ? get(yield select(selectCbSession), 'isLoggedIn')
    : false;
  try {
    const apolloClient = yield select(getApolloClient);
    const apolloClientMeResponse = yield apolloClient.query({ query: meQuery });
    const {
      teacherSubjects,
      studentSubjects,
      adminSubjects,
      educationPeriods,
      currentEducationPeriod,
      nextEducationPeriod,
      me,
    } = yield apolloClientMeResponse.data;
    const { heap_enabled, heap_for_students_enabled } = yield fetchFlags(
      ['heap_enabled', 'heap_for_students_enabled'],
      get(me, 'initId', 1),
    );
    const successObject = {
      me,
      teacherSubjects,
      studentSubjects,
      adminSubjects,
      educationPeriod: {
        educationPeriods,
        currentEducationPeriod,
        nextEducationPeriod,
      },
    };
    yield setGlobalCurrentUser(me);

    const isTeacher = me.userRole.includes('teacher');
    const isStudent = me.userRole.includes('student');

    const heapEnabled = heap_enabled === 'on';
    const heapForStudentsEnabled = heap_for_students_enabled === 'on';

    if (heapEnabled) {
      const shouldCaptureReplay =
        isTeacher || (isStudent && heapForStudentsEnabled);
      initHeap(me, shouldCaptureReplay);
    }

    // if is an SSO user but no session exists in cbSession object, we push them back to re-login on MyAP
    if (
      !OVERRIDE_SSO_LOGIN &&
      me.ssoLogin === 'SSO-FYM' &&
      !cbSessionIsLoggedIn &&
      !isLockdownBrowser()
    ) {
      // We do this after authorizing, because they can be a direct login user.
      // If they have a token, and we presume they have an existing session and try to log them in.
      // After running through this service, we do a comparison to see if this is an SSO-user and they are logged in as SSO.
      if (isLogin) {
        yield put(login.failure('SSO user, not logged in'));
      } else {
        yield put(authorize.failure('SSO user, not logged in'));
      }
    } else if (isLogin) {
      yield put(login.success(successObject));
    } else {
      yield put(authorize.success(successObject));
    }
    if (
      isOnLogin(payload.location.pathname) ||
      isOnRoot(payload.location.pathname)
    ) {
      yield put(redirectToHomeAction(payload));
    } else {
      yield put(pageLoading.fulfill());
    }
  } catch (error) {
    log('authorizeSerivceSaga error', error);
    yield log('error', error);
    if (isLogin) {
      yield put(login.failure(error));
    } else {
      yield put(authorize.failure(error));
    }
    yield put(pageLoading.fulfill());
  }
}

export function* redirectToHomeSaga({ payload: { router, location } }) {
  try {
    const user = yield select(getUserData);
    const { me, studentSubjects, teacherSubjects, adminSubjects } = user;
    const redirectPath = location.query.redirect_path;
    const userSubjects =
      !!me &&
      (me.isStudent
        ? studentSubjects
        : me.isAdmin
          ? adminSubjects
          : teacherSubjects);
    const isAdmin = me.userRole.includes('admin');
    const isCoordinator = me.userRole.includes('coordinator');
    const noRoute =
      !redirectPath && !isAdmin && !isCoordinator && !userSubjects[0];
    if (redirectPath) {
      window.location.href = redirectPath;
    }
    if (isAdmin) {
      router.replace('/admin');
      return false;
    }
    if (isCoordinator) {
      router.replace('/coordinator');
      return false;
    }
    if (userSubjects[0]) {
      userSubjects && userSubjects.length > 1
        ? router.replace('/subjects/preview')
        : router.replace(`/${userSubjects[0].id}/home`);
      return false;
    }
    if (noRoute) {
      log(`hit the noRoute`);
      yield put(redirectToROSLoginAction());
    }
    yield put(pageLoading.fulfill());
  } catch (error) {
    log('redirectToHomeSaga error', error);
    yield put(redirectToHomeAction.failure(error));
  }
}

export default function* authSaga() {
  yield all([
    takeLatest(authenticateStrategyAction.TRIGGER, authenticateStrategySaga),
    takeLatest(levityLoginAction.TRIGGER, serviceAccountsSaga),
    takeLatest([login.TRIGGER, authorize.TRIGGER], authorizeServiceSaga),
    takeLatest(redirectToHomeAction.TRIGGER, redirectToHomeSaga),
    takeLatest(
      [
        redirectToLoginAction.TRIGGER,
        redirectToROSLoginAction.TRIGGER,
        login.FAILURE,
        authenticateStrategyAction.FAILURE,
        authorize.FAILURE,
        levityLoginAction.FAILURE,
      ],
      redirectToROSLoginSaga,
    ),
  ]);
}
